<script>
	import Popover from './popover.svelte'
	import { onMount } from 'svelte'
  import debounce from 'debounce'
  import { LoaderCircle, Ellipsis, X } from 'lucide-svelte'
  
	let { 
    open = $bindable(false),
    value = $bindable([]),
    items = $bindable([]),
    labelKey = "label",
    valueKey = "value",
    queryKey = "q",
    load,
    url,
    name,
    loadAll = false,
    fetchInitial = true,
    onValueChange,
    ...inputProps
  } = $props()

	let index = $state(0)
	let query = $state("")
  let typing = $state(false)
  let loading = $state(false)
  let inputRef = $state(null)
  let itemRefs = $state([])
  // We maintain a manual list of selected items to preserve order of selection as opposed to deriving from options
  let selectedItems = $state([])

  const debouncedLoad = load ? debounce(load, 300) : null
  const debouncedFetch = url ? debounce(fetchFromUrl, 300) : null

	onMount(async () => {
    if (url && (loadAll || fetchInitial)) {
      await fetchFromUrl({ includeQuery: false, includeIds: !loadAll })
    }
		// selectedItem && selectItem(selectedItem)
    selectedItems = items.filter(item => value.includes(item[valueKey]))
	})
  
  // An effect that smooth scrolls the highlighted item into view
  $effect(() => {
    if (itemRefs[index]) {
      itemRefs[index]?.scrollIntoView({ behavior: "instant", block: "nearest" })
    }
  })

  // An effect that runs when the value changes

	let filteredItems = $derived.by(() => {
		return items.filter((item) => {
      const isSelected = value.length && value.includes(item[valueKey])
      const matchesQuery = item[labelKey]?.toLowerCase().includes(query.toLowerCase())
			return matchesQuery && !isSelected
		})
	})

	let highlightedItem = $derived(filteredItems[index])
	let selectedItem = $derived(value && items.find(item => item[valueKey] === value))

  async function fetchFromUrl({ includeQuery = true, includeIds = false } = {}) {
    typing = false
    if (!url || (!query.length && !loadAll)) return
    loading = true
    const fetchUrl = new URL([window.location.origin, url].join('/'))
    if (includeQuery) fetchUrl.searchParams.set(queryKey, query)
    if (includeIds) fetchUrl.searchParams.append("ids[]", value)
    const response = await fetch(fetchUrl.toString())
    const data = await response.json()
    items = data
    loading = false
  }
	
  function handleUpKey() {
    index = Math.max(index - 1, 0)
  }

  function handleDownKey() {
    if (!open) return open = true
    index = Math.min(index + 1, items.length - 1)
  }

  function handleEnterKey(e) {
    e.preventDefault()
    e.stopPropagation()
    if (Boolean(highlightedItem)) {
      handleSelect()
    }
  }

  function handleEscapeKey() {
    closeList()
  }

  function handleBackspaceKey() {
    if (!!query.length) return
    removeLastItem()
  }

	function handleKeydown(e) {
    if (e.key === "ArrowUp") {
      handleUpKey()
    } else if (e.key === "ArrowDown") {
      handleDownKey()
    } else if (e.key === "Enter") {
      handleEnterKey(e)
    } else if (e.key === "Escape") {
      handleEscapeKey()
    } else if (e.key === "Backspace") {
      handleBackspaceKey()
    }
	}

	async function handleInput(e) {
    index = 0
    open = true
		if (!query.length) return
    if (url && !loadAll) {
      typing = true
      await debouncedFetch()
    }
    if (load) await debouncedLoad()
	}

	function handleSelect() {
		if (highlightedItem) {
      const newItems = Array.from(new Set([...selectedItems, highlightedItem]))
      selectedItems = newItems
      value = newItems.map(item => item[valueKey])
      query = ""
      index = 0
      onValueChange?.(value)
		}
	}

	function selectItem(item, e) {
		e?.preventDefault()
		e?.stopPropagation()
    const newItems = Array.from(new Set([...selectedItems, item]))
    selectedItems = newItems
    value = newItems.map(item => item[valueKey])
    query = ""
    index = 0
    onValueChange?.(value)
    closeList()
	}

  function closeList() {
    open = false
    index = 0
  }

  function removeLastItem() {
    selectedItems = selectedItems.slice(0, -1)
    value = selectedItems.map(item => item[valueKey])
    onValueChange?.(value)
  }

  function handleRemoveItem(item, e) {
    e.preventDefault()
    e.stopPropagation()
    selectedItems = selectedItems.filter(i => i[valueKey] !== item[valueKey])
    value = selectedItems.map(item => item[valueKey])
    onValueChange?.(value)
  }

  export function setValue(items, { triggerCallback = false } = {}) {
    value = items.map(item => item[valueKey])
    selectedItems = items
    query = ""
    if (triggerCallback) onValueChange?.(value)
  }
  
  export function clearValue(triggersCallback = true) {
    value = []
    query = ""
    if (triggersCallback) onValueChange?.(value)
  }

  export function focus() {
    inputRef?.focus()
  }

</script>

<Popover bind:open placement="bottom-start">
	{#snippet trigger()}
    <div class="input-container flex flex-wrap gap-1 h-auto min-h-10 items-center px-3 border border-gray-200 relative focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">

      {#each selectedItems as item (item[valueKey])}
        <div class="bg-blue-400 text-white pl-2 pr-1 py-1 rounded-sm text-xs w-max flex items-center gap-1 flex-shrink-0">
          <span>{item[labelKey]}</span>
          <button type="button" class="w-4 h-4 flex items-center justify-center cursor-pointer" onmousedown={(e) => handleRemoveItem(item, e)} tabindex="-1">
            <X size={12} class="text-white" />
          </button>
        </div>
      {/each}
      
      <input
        bind:this={inputRef}
        bind:value={query}
        onfocus={() => open = true}
        onclick={() => open = true}
        onblur={closeList}
        onkeydown={handleKeydown}
        oninput={handleInput}
        autocomplete="off"
        class="w-auto flex-1 h-7! text-gray-600 text-smaller p-0! focus:outline-none"
        {...inputProps}
      />
      {#if loading || typing}
        <div class="loading">
          {#if loading}
            <LoaderCircle size={16} /> 
          {:else}
            <Ellipsis size={16} />
          {/if}
        </div>
      {/if}
    </div>
	{/snippet}
	
  {#snippet contents({ maxHeight })}
    
  {@const preferredMaxHeight = Math.min(maxHeight, 300) }
		
    <div class="list" style={`max-height: ${preferredMaxHeight}px`}>
      {#if typing}
        <div class="item informational">En train de taper...</div>
      {:else if loading}
        <div class="item informational">Chargement...</div>
      {:else}
        {#each filteredItems as item, idx (item[valueKey])}
          <div 
            bind:this={itemRefs[idx]}
            class="item" 
            class:highlighted={idx === index}
            class:selected={item[valueKey] === value}
            onpointerdown={(e) => selectItem(item, e)}
            >{item[labelKey]}</div>
        {:else}
          <div class="item informational">{items.length ? "Aucun résultat" : "Aucun élément disponible"}</div>
        {/each}
      {/if}
		</div>
	{/snippet}
</Popover>

{#if name}
  <input type="hidden" name={name} value={value ?? ""} />
{/if}

<style>
	.list {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-radius: 0.5rem;
    padding: 0.4rem 0;
    border: 1px solid #e0e0e0;
    background: white;
    overflow-y: auto;
    width: 100%;
    &.empty {
      padding: 0;
      border: none;
      box-shadow: none;
    }
	}
	.item {
		background: white;
		color: #374151;
		padding: 0.5rem 0.85rem;
		font-size: 0.8rem;
    border-bottom: 1px solid #f3f4f6;
    cursor: pointer;
    &:last-child {
      border-bottom: none;
    }
    &:hover {
      background: #f3f4f6;
    }
		&.highlighted {
			background: #eff6ff;
			color: #1f2937;
		}
    &.selected {
      background: #eff6ff;
      color: #1f2937;
    }
    &.informational {
      color: #888;
      pointer-events: none;
    }
	}

  .loading {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
  }
</style>